'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { SITE_CONTAINER } from '@/lib/site-layout'
import { formatSiteTime } from '@/lib/site-datetime'

interface News {
  id: number
  title: string
  slug: string
  createdAt?: string | Date | null
  category: {
    slug: string
  } | null
}

interface BreakingNewsBarProps {
  news: News[]
}

function formatItemTime(v: unknown) {
  try {
    if (!v) return null
    const d = v instanceof Date ? v : new Date(String(v))
    if (!Number.isFinite(d.getTime())) return null
    return formatSiteTime(d, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  } catch {
    return null
  }
}

function BreakingItem({ item }: { item: News }) {
  const time = formatItemTime(item.createdAt)
  return (
    <Link
      href={`/${item.category?.slug || 'haber'}/${item.slug}-${item.id}`}
      className="flex min-w-0 items-center gap-2 text-[13px] font-semibold leading-none !text-white hover:!text-white hover:underline"
    >
      {time && (
        <span className="w-10 shrink-0 text-right text-[11px] font-black tabular-nums tracking-tight !text-white">
          {time}
        </span>
      )}
      <span className="shrink-0 text-[10px] !text-white" aria-hidden="true">
        •
      </span>
      <span className="truncate !text-white">{item.title}</span>
    </Link>
  )
}

export default function BreakingNewsBar({ news }: BreakingNewsBarProps) {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (news.length <= 1) return
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % news.length)
    }, 3_000)
    return () => window.clearInterval(id)
  }, [news.length])

  if (news.length === 0) return null

  const safeIndex = ((index % news.length) + news.length) % news.length
  const current = news[safeIndex]

  return (
    <div className="bg-red-700 py-1 text-white" role="alert" aria-live="polite">
      <div className={`${SITE_CONTAINER} flex h-7 items-center gap-2`}>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex shrink-0 items-center gap-1.5">
            <div className="breaking-news-label shrink-0 whitespace-nowrap rounded bg-white px-2 py-[3px] text-[10px] font-extrabold uppercase leading-none tracking-wider text-gray-900 dark:bg-slate-800 dark:text-gray-100 sm:px-2.5 sm:text-[11px]">
              <span className="sm:hidden">Son</span>
              <span className="hidden sm:inline">Son Gelişmeler</span>
            </div>

            {news.length > 1 ? (
              <div className="hidden items-center gap-1 sm:flex">
                <button
                  type="button"
                  aria-label="Önceki"
                  onClick={() =>
                    setIndex((i) => (i - 1 + news.length) % news.length)
                  }
                  className="flex size-6 items-center justify-center rounded text-white transition-colors hover:bg-white/15"
                >
                  <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true">
                    <path fill="currentColor" d="M15.5 19L8.5 12l7-7 1.4 1.4L11.3 12l5.6 5.6z" />
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label="Sonraki"
                  onClick={() => setIndex((i) => (i + 1) % news.length)}
                  className="flex size-6 items-center justify-center rounded text-white transition-colors hover:bg-white/15"
                >
                  <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true">
                    <path fill="currentColor" d="M8.5 19l-1.4-1.4 5.6-5.6-5.6-5.6L8.5 5l7 7z" />
                  </svg>
                </button>
              </div>
            ) : null}
          </div>

          <div className="min-w-0 flex-1">
            <BreakingItem item={current} />
          </div>
        </div>
      </div>
    </div>
  )
}
